import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { KeyRound, Loader2, CheckCircle2, XCircle, Info, Trash2, AlertCircle } from 'lucide-react';
import { getSettings, setGeminiByokKey, toggleByok, deleteGeminiByokKey } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { track } from '../../lib/telemetry';
import type { AiKeyConfig, CompanySettings } from '../../types';

export function GeminiKeySettings() {
  const qc = useQueryClient();
  const [keyInput, setKeyInput] = useState('');
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: queryKeys.settings,
    queryFn: getSettings,
  });

  const keyConfig = settings?.aiKeyConfig;
  const useByok = keyConfig?.useByok ?? false;
  const hasStoredKey = Boolean(keyConfig?.keyLast4);

  const saveMutation = useMutation({
    mutationFn: (apiKey: string) => setGeminiByokKey(apiKey),
    onSuccess: () => {
      track('settings.byok_key_saved', 'action', {});
      setKeyInput('');
      setShowKeyForm(false);
      void qc.invalidateQueries({ queryKey: queryKeys.settings });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (next: boolean) => toggleByok(next),
    onMutate: async (next) => {
      setToggleError(null);
      await qc.cancelQueries({ queryKey: queryKeys.settings });
      const previous = qc.getQueryData<CompanySettings>(queryKeys.settings);
      // Optimistic flip so the pill responds immediately instead of waiting
      // on a round trip + refetch before anything visibly changes.
      qc.setQueryData<CompanySettings>(queryKeys.settings, (old) => {
        if (!old) return old;
        const prevConfig = old.aiKeyConfig;
        const aiKeyConfig: AiKeyConfig = prevConfig
          ? { ...prevConfig, useByok: next }
          : {
              useByok: next,
              keyLast4: null,
              configuredAt: null,
              validatedAt: null,
              status: null,
              lastError: null,
              lastErrorAt: null,
            };
        return { ...old, aiKeyConfig };
      });
      return { previous };
    },
    onError: (err, _next, context) => {
      if (context?.previous) qc.setQueryData(queryKeys.settings, context.previous);
      setToggleError(
        err instanceof Error ? err.message : 'Failed to switch the AI key source. Please try again.'
      );
    },
    onSuccess: (_, next) => {
      track('settings.byok_toggled', 'action', { useByok: next });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.settings });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteGeminiByokKey(),
    onSuccess: () => {
      track('settings.byok_key_deleted', 'action', {});
      void qc.invalidateQueries({ queryKey: queryKeys.settings });
    },
  });

  const selectPlatform = () => {
    if (useByok) toggleMutation.mutate(false);
  };

  const selectByok = () => {
    if (hasStoredKey && !useByok) {
      toggleMutation.mutate(true);
    } else if (!hasStoredKey) {
      setShowKeyForm(true);
    }
  };

  const handleSave = () => {
    if (!keyInput.trim()) return;
    saveMutation.mutate(keyInput.trim());
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(2)].map((_, i) => <div key={i} className="skeleton h-24 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h3 className="text-title text-gray-900 dark:text-white flex items-center gap-1.5">
          <KeyRound className="h-4 w-4 text-primary" />
          AI Key
        </h3>
        <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5">
          Choose which key powers this workspace's AI features (risk scoring, briefings, insights, generated actions and documents).
        </p>
      </div>

      {/* Segmented choice */}
      <div className="flex gap-2 p-1 bg-gray-100 dark:bg-white/[0.04] rounded-xl w-fit">
        <button
          type="button"
          onClick={selectPlatform}
          disabled={toggleMutation.isPending}
          className={`px-4 py-2 rounded-lg text-meta font-medium transition-colors focus-visible:ring-2 focus-visible:ring-primary/50 ${
            !useByok
              ? 'bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Use OctoPM AI
        </button>
        <button
          type="button"
          onClick={selectByok}
          disabled={toggleMutation.isPending}
          className={`px-4 py-2 rounded-lg text-meta font-medium transition-colors focus-visible:ring-2 focus-visible:ring-primary/50 ${
            useByok
              ? 'bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Bring your own key
        </button>
      </div>

      {toggleError && (
        <div className="flex items-start gap-2 text-meta text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-3">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p>{toggleError}</p>
        </div>
      )}

      {!useByok && !showKeyForm && (
        <p className="text-meta text-gray-400 dark:text-gray-500">
          Your workspace currently uses OctoPM AI.
        </p>
      )}

      {/* Gemini specifics belong only here, next to the key itself — not in
          the generic OctoPM-AI state above. */}
      {(useByok || showKeyForm) && (
        <div className="flex items-start gap-2 text-micro text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-lg p-3">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-400" />
          <p>
            Bring a Gemini API key from your own Google Cloud project. It draws on that project's own
            quota and billing — separate from every other OctoPM workspace — and costs land on your
            Google bill. If it hits its quota or is revoked, AI features pause for your workspace; we
            do not silently fall back to OctoPM's key.
          </p>
        </div>
      )}

      {useByok && !showKeyForm && hasStoredKey && (
        <div className="bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-body font-medium text-gray-900 dark:text-white font-mono">
                •••• {keyConfig?.keyLast4}
              </p>
              <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5">
                {keyConfig?.validatedAt
                  ? `Validated ${new Date(keyConfig.validatedAt).toLocaleString()}`
                  : 'Not yet validated'}
              </p>
            </div>
            {keyConfig?.status === 'error' ? (
              <span className="flex items-center gap-1.5 text-meta font-medium text-red-600 dark:text-red-400">
                <XCircle className="h-4 w-4" /> Failing
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-meta font-medium text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" /> Active
              </span>
            )}
          </div>

          {keyConfig?.status === 'error' && keyConfig?.lastError && (
            <div className="text-meta text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-3">
              <p className="font-medium">Your key is failing — AI calls are NOT falling back to OctoPM's key.</p>
              <p className="mt-1">{keyConfig.lastError}</p>
              {keyConfig?.lastErrorAt && (
                <p className="mt-1 text-micro text-red-500 dark:text-red-400/80">
                  Last seen {new Date(keyConfig.lastErrorAt).toLocaleString()}
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowKeyForm(true)}
              className="px-3 py-1.5 rounded-lg text-meta font-medium text-primary hover:bg-primary/10 transition-colors focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              Replace key
            </button>
            <button
              type="button"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-meta font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {deleteMutation.isPending ? 'Removing…' : 'Remove key'}
            </button>
          </div>
        </div>
      )}

      {(showKeyForm || (useByok && !hasStoredKey)) && (
        <div className="bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <label className="block text-meta font-medium text-gray-700 dark:text-gray-400">
              Gemini API key
            </label>
            <a
              href="https://aistudio.google.com/apikey"
              target="_blank"
              rel="noreferrer"
              className="text-micro text-primary hover:text-primary/80 transition-colors focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              Get a key →
            </a>
          </div>
          <input
            type="password"
            autoComplete="off"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="AIzaSy…"
            className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta font-mono focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 placeholder-gray-400"
          />
          <p className="text-meta text-gray-400 dark:text-gray-500">
            We make a real test call before saving anything — an invalid key is rejected here, not after it's stored.
          </p>

          {saveMutation.isError && (
            <p className="text-meta text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-3">
              {(saveMutation.error as Error).message}
            </p>
          )}

          <div className="flex gap-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={saveMutation.isPending || !keyInput.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-meta font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              {saveMutation.isPending ? 'Validating…' : 'Validate & Save'}
            </motion.button>
            {hasStoredKey && (
              <button
                type="button"
                onClick={() => { setShowKeyForm(false); setKeyInput(''); }}
                className="px-4 py-2 rounded-xl text-meta font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors focus-visible:ring-2 focus-visible:ring-primary/50"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
